import { PLAYER_COLORS } from "@grundbesitz/shared";
import type { PlayerColorId, TokenId } from "@grundbesitz/shared";
import type { ReactNode } from "react";

/**
 * The twelve figures, drawn in one style: 48 x 48 units, one dark outline, body filled with the player color,
 * a few soft white highlights and dark details. Beaks are the only fixed color.
 */

const INK = "#1b1a17";
const PAPER = "#fffdf8";
const BEAK = "#f4a340";
const FALLBACK = "#8a8f98";

const HIGHLIGHT = { fill: "none", stroke: "#fff", strokeOpacity: 0.55, strokeWidth: 2.4 } as const;

function shapes(token: TokenId, c: string): ReactNode {
  switch (token) {
    case "hat":
      return (
        <>
          <path d="M12 34V11q0-4 4-4h16q4 0 4 4v23Z" fill={c} />
          <rect x="12" y="24" width="24" height="6" fill={INK} stroke="none" />
          <path d="M12 24h24M12 30h24" fill="none" strokeWidth="1.6" />
          <ellipse cx="24" cy="35" rx="20" ry="5.5" fill={c} />
          <path d="M17 11v9" {...HIGHLIGHT} />
        </>
      );
    case "thimble":
      return (
        <>
          <path d="M12 37l2-18q1-11 10-11t10 11l2 18Z" fill={c} />
          <rect x="9" y="36" width="30" height="6.5" rx="3.25" fill={c} />
          <g fill={INK} stroke="none">
            <circle cx="21" cy="14" r="1.4" />
            <circle cx="27" cy="14" r="1.4" />
            <circle cx="18" cy="20" r="1.4" />
            <circle cx="24" cy="20" r="1.4" />
            <circle cx="30" cy="20" r="1.4" />
            <circle cx="21" cy="26" r="1.4" />
            <circle cx="27" cy="26" r="1.4" />
            <circle cx="17" cy="31.5" r="1.4" />
            <circle cx="23" cy="31.5" r="1.4" />
            <circle cx="29" cy="31.5" r="1.4" />
          </g>
        </>
      );
    case "iron":
      return (
        <>
          <path d="M10 24V15q0-6 7-6h13q7 0 7 6v9Z" fill={c} />
          <path d="M16 24v-8q0-2.5 2.5-2.5h11q2.5 0 2.5 2.5v8Z" fill={INK} stroke="none" />
          <path d="M4 38V29q0-5 5-5h20q8 0 13 14Z" fill={c} />
          <rect x="3" y="35" width="42" height="5" rx="2.5" fill={INK} />
          <g fill="#fff" fillOpacity="0.7" stroke="none">
            <circle cx="12" cy="31" r="1.6" />
            <circle cx="19" cy="31" r="1.6" />
            <circle cx="26" cy="31" r="1.6" />
          </g>
        </>
      );
    case "battleship":
      return (
        <>
          <rect x="34" y="22.5" width="11" height="3.6" rx="1.4" fill={INK} />
          <path d="M22.5 15V6.5l6 2.2-6 2.2" fill={c} strokeWidth="1.8" />
          <rect x="19" y="14" width="8" height="8" rx="1" fill={c} />
          <rect x="13" y="21" width="20" height="8" rx="1.5" fill={c} />
          <path d="M3 29h42l-6 11H10Z" fill={c} />
          <path d="M16 25h3M22 25h3M28 25h3" fill="none" stroke={INK} strokeWidth="1.8" />
          <path d="M2 44q5.2-3.4 10.4 0t10.4 0 10.4 0 10.4 0" fill="none" strokeWidth="1.8" />
        </>
      );
    case "boot":
      return (
        <>
          <path d="M9 5h16v15q0 3 4 4.5l10 4q6 2.4 6 8V41H9Z" fill={c} />
          <path d="M9 35h36v6.5q0 .5-.5.5h-35q-.5 0-.5-.5Z" fill={INK} />
          <path d="M13.5 11h7.5M13.5 17h7.5" {...HIGHLIGHT} />
        </>
      );
    case "wheelbarrow":
      return (
        <>
          <path d="M5 13h29l-5 14H11Z" fill={c} />
          <path d="M5 13 1.5 25M12 27 9.5 41M30 24l6 6" fill="none" strokeWidth="3" />
          <circle cx="38" cy="33" r="7.5" fill={INK} />
          <circle cx="38" cy="33" r="2.6" fill={PAPER} stroke="none" />
          <path d="M11 17h18" {...HIGHLIGHT} />
        </>
      );
    case "dog":
      return (
        <>
          <path d="M9 24Q4 18 7 11" fill="none" strokeWidth="3.4" />
          <path d="M9 22q0-4 5-4h16q5 0 5 6v9H12q-3 0-3-4Z" fill={c} />
          <rect x="11.5" y="30" width="5.5" height="11.5" rx="2" fill={c} />
          <rect x="28.5" y="30" width="5.5" height="11.5" rx="2" fill={c} />
          <path d="M30 21l3-11q1-2 4-2l7 5 3 5-4 4q-6 1-11 3Z" fill={c} />
          <path d="M33 10 31 3l7 4.5Z" fill={INK} />
          <circle cx="38.5" cy="13.5" r="1.5" fill={INK} stroke="none" />
          <circle cx="45.4" cy="17.4" r="1.8" fill={INK} stroke="none" />
          <path d="M14 23h12" {...HIGHLIGHT} />
        </>
      );
    case "racecar":
      return (
        <>
          <path d="M3 32v-4q0-4 5-4h9l5-7h10l6 7h3q5 1 5 6v2Z" fill={c} />
          <path d="M20 24l3.5-5h7.5l4 5Z" fill={PAPER} strokeWidth="1.8" />
          <path d="M4 25V19h5" fill="none" strokeWidth="2.6" />
          <circle cx="13" cy="33" r="5.6" fill={INK} />
          <circle cx="37" cy="33" r="5.6" fill={INK} />
          <circle cx="13" cy="33" r="1.9" fill={PAPER} stroke="none" />
          <circle cx="37" cy="33" r="1.9" fill={PAPER} stroke="none" />
          <path d="M9 28h28" {...HIGHLIGHT} strokeWidth="2" />
        </>
      );
    case "cat":
      return (
        <>
          <path d="M34 42q12 0 9-12" fill="none" strokeWidth="4" />
          <path d="M13 43Q9 28 24 26t11 17Z" fill={c} />
          <path d="M14.5 14 14 2.5 22.5 8Z" fill={c} />
          <path d="M33.5 14 34 2.5 25.5 8Z" fill={c} />
          <circle cx="24" cy="17" r="11" fill={c} />
          <circle cx="20" cy="16" r="1.6" fill={INK} stroke="none" />
          <circle cx="28" cy="16" r="1.6" fill={INK} stroke="none" />
          <path d="M22.4 20h3.2L24 21.8Z" fill={INK} strokeWidth="1.2" />
          <path d="M18 21.5 12 23M18 23.5 12.5 26M30 21.5 36 23M30 23.5 35.5 26" fill="none" strokeWidth="1.1" />
          <path d="M19 32v9M29 32v9" {...HIGHLIGHT} strokeWidth="2" />
        </>
      );
    case "dinosaur":
      return (
        <>
          <path d="M3 37q9-1 12-9" fill="none" strokeWidth="3.6" />
          <ellipse cx="20" cy="29" rx="14" ry="9" fill={c} />
          <path d="M29 24q6-3 5-12h-2q0-4 4-5h5q3 0 3 3.5T41 15h-2q0 10-6 16Z" fill={c} />
          <rect x="11" y="33" width="5.5" height="9" rx="2" fill={c} />
          <rect x="25" y="33" width="5.5" height="9" rx="2" fill={c} />
          <circle cx="40.5" cy="9.6" r="1.4" fill={INK} stroke="none" />
          <path d="M11 22l2.5-4 2.5 4M17.5 21l2.5-4 2.5 4M24 21l2.5-4 2.5 4" fill={c} strokeWidth="1.8" />
          <path d="M9 30h14" {...HIGHLIGHT} />
        </>
      );
    case "duck":
      return (
        <>
          <path d="M9 27 2 19l13 4Z" fill={c} />
          <ellipse cx="22" cy="31" rx="17" ry="10.5" fill={c} />
          <circle cx="33" cy="15" r="8.5" fill={c} />
          <path d="M39.6 13.5 47 16l-7 3.4Z" fill={BEAK} />
          <circle cx="35.5" cy="12.6" r="1.5" fill={INK} stroke="none" />
          <path d="M12 31q8-6 15 0-8 6-15 0Z" fill="#fff" fillOpacity="0.45" strokeWidth="1.8" />
        </>
      );
    case "penguin":
      return (
        <>
          <ellipse cx="18" cy="43.5" rx="5" ry="2.2" fill={BEAK} />
          <ellipse cx="30" cy="43.5" rx="5" ry="2.2" fill={BEAK} />
          <path d="M11 24Q4 31 8 39q5-3 6-9Z" fill={c} />
          <path d="M37 24q7 7 3 15-5-3-6-9Z" fill={c} />
          <ellipse cx="24" cy="25" rx="13.5" ry="18.5" fill={c} />
          <ellipse cx="24" cy="30.5" rx="8" ry="11.5" fill={PAPER} stroke="none" />
          <circle cx="19.5" cy="14" r="2.6" fill={PAPER} strokeWidth="1.6" />
          <circle cx="28.5" cy="14" r="2.6" fill={PAPER} strokeWidth="1.6" />
          <circle cx="19.8" cy="14.2" r="1.1" fill={INK} stroke="none" />
          <circle cx="28.2" cy="14.2" r="1.1" fill={INK} stroke="none" />
          <path d="M21 18h6l-3 4.4Z" fill={BEAK} strokeWidth="1.6" />
        </>
      );
  }
}

const COLOR_HEX = new Map<string, string>(PLAYER_COLORS.map((p) => [p.id, p.hex]));

export function TokenIcon({ token, color, size = 32, title }: { token: TokenId; color?: PlayerColorId; size?: number; title?: string }) {
  const fill = (color && COLOR_HEX.get(color)) || FALLBACK;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <g stroke={INK} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round">
        {shapes(token, fill)}
      </g>
    </svg>
  );
}
